import React, { useState, useEffect } from 'react';
import axios from 'axios';
import dayjs from 'dayjs'; // Make sure dayjs is imported
import '../css/Home.css';
import api from './api';

const IstaAdminPanel = () => {
  // Get current date using dayjs
  const today = dayjs();
  
  // Calculate date from 3 months ago
  const threeMonthsAgo = today.subtract(3, 'month');
  
  // Calculate date for one week ago (to avoid trying to fetch current week's incomplete data)
  const oneWeekAgo = today.subtract(7, 'day');
  
  // Format the dates for input fields
  const defaultFromDate = threeMonthsAgo.format('YYYY-MM-DD');
  const defaultToDate = oneWeekAgo.format('YYYY-MM-DD');  // Default to 7 days ago
  
  // Get current year for other default values (keeping this for other sections)
  const currentYear = today.year();
  const currentMonth = today.month() + 1; // dayjs months are also 0-indexed
  
  // State for basic weekly data sync - now using the 3 month range
  const [from, setFrom] = useState(defaultFromDate);
  const [to, setTo] = useState(defaultToDate);
  const [log, setLog] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null); // { current, total, label }
  
  // If we're past July 1st, use current year for default end year, otherwise previous year
  const defaultEndYear = currentMonth >= 7 ? currentYear : currentYear - 1;
  
  // State for energy year import (keeping this calculation for this specific control)
  const [energyYearToImport, setEnergyYearToImport] = useState(defaultEndYear - 1);
  const [clearExisting, setClearExisting] = useState(true);
  
  // State for June 30 totals import
  const [june30Year, setJune30Year] = useState(defaultEndYear - 1);
  const [forceTotals, setForceTotals] = useState(true);
  const [yearlyTotalsOnly, setYearlyTotalsOnly] = useState(true);
  
  // NEW: State for gas comparison refresh
  const [gasComparisonMonths, setGasComparisonMonths] = useState(14); // Default to 14 months
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successStats, setSuccessStats] = useState(null);
  const [refreshProgress, setRefreshProgress] = useState(null); // { current, total, label }
  
  // State for tracking last update and last month
  const [lastUpdate, setLastUpdate] = useState(null);
  const [lastGasDataMonth, setLastGasDataMonth] = useState(null);
  const [lastGasDataYear, setLastGasDataYear] = useState(null);
  const [lastGasDataUpdateTime, setLastGasDataUpdateTime] = useState(null);
  const [activeSection, setActiveSection] = useState('gas'); // Track which section is showing logs

  // Fetch last update date and last gas data month on component mount
  useEffect(() => {
    const fetchLastDate = async () => {
      try {
        const res = await api.get(`${process.env.REACT_APP_API_URL}/api/ista/last-date`);
        if (res.data && res.data.lastDate) {
          setLastUpdate(new Date(res.data.lastDate).toLocaleDateString('nl-NL'));
        }
      } catch (err) {
        console.error('Error fetching last update date:', err);
      }
    };
    
    const fetchLastGasMonth = async () => {
      try {
        const res = await api.get(`${process.env.REACT_APP_API_URL}/api/energy/gas-comparison/last-month`);
        if (res.data && res.data.success) {
          // Format month and year for display
          const monthNames = {
            1: 'januari', 2: 'februari', 3: 'maart', 4: 'april',
            5: 'mei', 6: 'juni', 7: 'juli', 8: 'augustus',
            9: 'september', 10: 'oktober', 11: 'november', 12: 'december'
          };
          
          const month = res.data.lastMonth;
          const year = res.data.lastYear;
          
          if (month && year) {
            const formattedMonth = monthNames[month] || month;
            setLastGasDataMonth(formattedMonth);
            setLastGasDataYear(year);
            
            // Calculate months needed to update
            const currentDate = dayjs();
            const lastDataDate = dayjs(`${year}-${month.toString().padStart(2, '0')}-01`);
            const monthsDiff = currentDate.diff(lastDataDate, 'month');
            
            // If last data is in current month, suggest 1 month update
            const suggestedMonths = Math.max(1, monthsDiff);
            setGasComparisonMonths(suggestedMonths);
            
            // Set the last update time if available
            if (res.data.lastUpdated) {
              setLastGasDataUpdateTime(new Date(res.data.lastUpdated).toLocaleDateString('nl-NL'));
            }
          }
        }
      } catch (err) {
        console.error('Error fetching last gas data month:', err);
      }
    };
    
    fetchLastDate();
    fetchLastGasMonth();
  }, []);

  // Auto-hide success message after 10 seconds
  useEffect(() => {
    let timer;
    if (showSuccessMessage) {
      timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 10000); // 10 seconds
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showSuccessMessage]);

  // Handler for weekly data sync — one week at a time
  const handleSync = async () => {
    setIsLoading(true);
    setLog('');
    setSyncProgress(null);
    setActiveSection('weekly');

    // Build list of weeks between from and to
    const weeks = [];
    let cursor = dayjs(from).startOf('week');
    const end = dayjs(to);
    while (cursor.isBefore(end)) {
      weeks.push({
        start: cursor.format('YYYY-MM-DD'),
        end: cursor.add(6, 'day').format('YYYY-MM-DD')
      });
      cursor = cursor.add(7, 'day');
    }

    let processed = 0;
    let errors = 0;

    for (let i = 0; i < weeks.length; i++) {
      const { start, end: weekEnd } = weeks[i];
      setSyncProgress({ current: i + 1, total: weeks.length, label: start });

      try {
        await api.get(`${process.env.REACT_APP_API_URL}/api/ista/weekly-usage/week`, {
          params: { start, end: weekEnd }
        });
        processed++;
      } catch (err) {
        errors++;
        console.error(`Error for week ${start}:`, err.response?.data?.error || err.message);
      }
    }

    setSyncProgress(null);

    const logMessage = errors === 0
      ? `✅ Sync complete — ${processed} weken verwerkt`
      : `⚠️ Klaar: ${processed} weken verwerkt, ${errors} fout(en).`;

    setLog(logMessage);
    setIsLoading(false);
  };

  // Handler for June 30 import
  const handleJune30Import = async () => {
    setIsLoading(true);
    setLog('');
    setActiveSection('june30');
    try {
      // Pass the selected year and options to the API
      const res = await api.get(`${process.env.REACT_APP_API_URL}/api/ista/import-june-30`, {
        params: {
          year: june30Year,
          forceTotals: forceTotals,
          yearlyTotalsOnly: yearlyTotalsOnly
        }
      });
      
      // Format response data for display
      if (typeof res.data === 'object') {
        const formattedResponse = `✅ June 30, ${june30Year} totals imported!\n\n` +
          `Stats:\n` +
          `- Total processed: ${res.data.stats?.totalProcessed || 0}\n` +
          `- Valid yearly totals: ${res.data.stats?.validYearlyTotals || 0}\n` +
          `- Skipped entries: ${res.data.stats?.skippedEntries || 0}\n` +
          `- Errors: ${res.data.stats?.errorEntries || 0}\n\n` +
          `${res.data.entries ? `Processed ${res.data.entries.length} apartments.` : ''}`;
        
        setLog(formattedResponse);
      } else {
        setLog(res.data || `✅ June 30, ${june30Year} totals imported!`);
      }
    } catch (err) {
      setLog(`❌ Error: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for energy year import
  const handleImportEnergyYear = async () => {
    setIsLoading(true);
    setLog('');
    setActiveSection('energyYear');
    try {
      const res = await api.get(`${process.env.REACT_APP_API_URL}/api/ista/import-energy-year`, {
        params: {
          year: energyYearToImport,
          clearExisting: clearExisting
        }
      });
      
      if (res.data && res.data.stats) {
        setLog(`✅ Energy year ${res.data.stats.energyYear} imported successfully!\n` +
               `   - Processed ${res.data.stats.processedMonths} months\n` +
               `   - Imported ${res.data.stats.totalRecords} records\n\n` +
               `The data will be available in the energy usage charts.`);
      } else {
        setLog(JSON.stringify(res.data, null, 2));
      }
    } catch (err) {
      setLog(`❌ Error: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Build the list of months to process based on gasComparisonMonths setting
  const buildMonthList = () => {
    const now = dayjs();
    const months = [];
    let start;

    if (gasComparisonMonths === 0) {
      start = dayjs('2021-07-01');
    } else {
      start = now.subtract(gasComparisonMonths, 'month').startOf('month');
    }

    let cursor = start;
    while (cursor.isBefore(now) || cursor.isSame(now, 'month')) {
      months.push({ year: cursor.year(), month: cursor.month() + 1 });
      cursor = cursor.add(1, 'month');
    }
    return months;
  };

  // Handler for refreshing gas comparison data — one month at a time
  const handleRefreshGasComparison = async () => {
    setIsLoading(true);
    setLog('');
    setShowSuccessMessage(false);
    setActiveSection('gas');
    setRefreshProgress(null);

    const monthNames = {
      1: 'januari', 2: 'februari', 3: 'maart', 4: 'april',
      5: 'mei', 6: 'juni', 7: 'juli', 8: 'augustus',
      9: 'september', 10: 'oktober', 11: 'november', 12: 'december'
    };

    const months = buildMonthList();
    let processed = 0;
    let errors = 0;

    for (let i = 0; i < months.length; i++) {
      const { year, month } = months[i];
      const label = `${monthNames[month]} ${year}`;
      setRefreshProgress({ current: i + 1, total: months.length, label });

      try {
        await api.get(`${process.env.REACT_APP_API_URL}/api/energy/refresh-gas-comparison/month`, {
          params: { year, month }
        });
        processed++;
      } catch (err) {
        errors++;
        console.error(`Error for ${label}:`, err.response?.data?.error || err.message);
      }
    }

    setRefreshProgress(null);

    // Refresh last gas month info
    try {
      const monthRes = await api.get(`${process.env.REACT_APP_API_URL}/api/energy/gas-comparison/last-month`);
      if (monthRes.data && monthRes.data.success) {
        const m = monthRes.data.lastMonth;
        const y = monthRes.data.lastYear;
        if (m && y) {
          setLastGasDataMonth(monthNames[m] || m);
          setLastGasDataYear(y);
          if (monthRes.data.lastUpdated) {
            setLastGasDataUpdateTime(new Date(monthRes.data.lastUpdated).toLocaleDateString('nl-NL'));
          }
        }
      }
    } catch (err) {
      console.error('Error updating last gas month info:', err);
    }

    const logMessage = errors === 0
      ? `✅ Gas comparison data bijgewerkt!\n- ${processed} maanden verwerkt`
      : `⚠️ Klaar met ${processed} maanden verwerkt, ${errors} fout(en). Controleer de server logs.`;

    setLog(logMessage);
    if (errors === 0) {
      setSuccessStats({ processedMonths: processed });
      setShowSuccessMessage(true);
    }
    setIsLoading(false);
  };

  // Utility functions remain unchanged
  const generateYearOptions = (startYear, endYear = currentYear + 1) => {
    const options = [];
    
    for (let year = startYear; year <= endYear; year++) {
      options.push(
        <option key={year} value={year}>
          {year}
        </option>
      );
    }
    
    return options;
  };

  const getEnergyYearLabel = (startYear) => {
    return `${startYear}-${startYear + 1}`;
  };

  // Calculate months difference for display
  const getMonthsNeededText = () => {
    if (!lastGasDataMonth || !lastGasDataYear) return '';
    
    const currentDate = dayjs();
    const lastDataMonth = Object.entries({
      'januari': 1, 'februari': 2, 'maart': 3, 'april': 4,
      'mei': 5, 'juni': 6, 'juli': 7, 'augustus': 8,
      'september': 9, 'oktober': 10, 'november': 11, 'december': 12
    }).find(([name]) => name === lastGasDataMonth)?.[1] || 1;
    
    const lastDataDate = dayjs(`${lastGasDataYear}-${lastDataMonth.toString().padStart(2, '0')}-01`);
    const monthsDiff = currentDate.diff(lastDataDate, 'month');
    
    if (monthsDiff <= 0) {
      return 'Up to date!';
    } else if (monthsDiff === 1) {
      return `1 month behind (${monthsDiff} month to update)`;
    } else {
      return `${monthsDiff} months behind (${monthsDiff} months to update)`;
    }
  };

  // Render the log section based on which section is active
  const renderLogSection = () => {
    if (!log) return null;
    
    return (
      <div className="mt-4 mb-4">
        <pre className="bg-gray-100 p-3 text-sm whitespace-pre-wrap rounded border border-gray-300 max-h-64 overflow-y-auto">
          {log}
        </pre>
      </div>
    );
  };

  return (
    <div className="home-container">
      <div className="home-overlay mx-auto p-6" style={{ maxWidth: '750px' }}>
        {/* Warning Banner - Made more prominent */}
        <div style={{ backgroundColor: '#cc0000', color: 'white', padding: '8px 16px', marginBottom: '24px', textAlign: 'center', fontWeight: 'bold', fontSize: '14px', border: '1px solid #cc0000', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          ⚠️ WAARSCHUWING: Dit panel bevat functies die data kunnen overschrijven. Lees de uitleg per sectie goed door.
        </div>
        
        <h2 className="text-2xl font-bold mb-6">🔧 ISTA Admin Panel</h2>
        
        {/* Success Message */}
        {showSuccessMessage && successStats && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex justify-between">
              <p className="text-green-800 font-semibold">
                ✅ Gas comparison data updated successfully!
              </p>
              <button 
                onClick={() => setShowSuccessMessage(false)}
                className="text-green-700 hover:text-green-900"
              >
                ×
              </button>
            </div>
            <div className="mt-2 text-sm text-green-700">
              <p>Processed {successStats.processedMonths} months of data</p>
            </div>
          </div>
        )}
        
        {lastUpdate && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-blue-800">
              📅 Last data update: <strong>{lastUpdate}</strong>
            </p>
          </div>
        )}

        {/* ENHANCED Gas Comparison Data Refresh Section */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h3 className="text-lg font-semibold mb-4">📊 Gas Comparison Data <span className="text-green-600 text-sm font-normal">(hier maak je niks mee stuk)</span></h3>
          
          <p className="mb-4 text-sm">
            <strong>Uitleg:</strong> Hiermee haal je maandelijks het verbruik van de Kraag en Toren op in GigaJoule bij Ista. Hiermee kun je het verbruik tussen de Kraag en Toren mee vergelijken.
          </p>
          
          {/* Enhanced Info Box for Last Gas Data Month */}
          {lastGasDataMonth && lastGasDataYear && (
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
              <p className="text-gray-700 text-sm font-semibold mb-1">
                📊 Data Status:
              </p>
              <ul className="text-gray-700 text-sm">
                <li><strong>Latest data:</strong> {lastGasDataMonth} {lastGasDataYear}</li>
                <li><strong>Status:</strong> <span className={getMonthsNeededText().includes('behind') ? 'text-orange-600 font-bold' : 'text-green-600 font-bold'}>{getMonthsNeededText()}</span></li>
                {lastGasDataUpdateTime && <li><strong>Last processed:</strong> {lastGasDataUpdateTime}</li>}
              </ul>
            </div>
          )}
          
          <div className="mb-4">
            <label className="block mb-1 font-semibold text-left">Months to refresh:</label>
            <select 
              value={gasComparisonMonths}
              onChange={e => setGasComparisonMonths(parseInt(e.target.value))}
              className="date-input mb-4"
            >
              <option value="0">All available data</option>
              <option value="1">Last 1 month</option>
              <option value="2">Last 2 months</option>
              <option value="3">Last 3 months</option>
              <option value="6">Last 6 months</option>
              <option value="12">Last 12 months</option>
              <option value="14">Last 14 months</option>
              <option value="24">Last 24 months</option>
            </select>
            
            <p className="text-xs text-gray-600 mt-1 mb-4">
              {gasComparisonMonths === 0 
                ? "This will refresh all historical data (may take several minutes)"
                : `This will refresh data from ${dayjs().subtract(gasComparisonMonths, 'month').format('MMMM YYYY')} to present`}
            </p>
          </div>
          
          {refreshProgress && (
            <div className="mb-4">
              <p className="text-sm text-gray-700 mb-1">
                Verwerken: <strong>{refreshProgress.label}</strong> ({refreshProgress.current}/{refreshProgress.total})
              </p>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${(refreshProgress.current / refreshProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          <button
            onClick={handleRefreshGasComparison}
            disabled={isLoading}
            className="home-button bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? 'Verwerken...' : '🔄 Refresh Gas Comparison Data'}
          </button>
          
          {/* Show logs directly below the gas section when it's active */}
          {activeSection === 'gas' && renderLogSection()}
        </div>
        
        {/* Weekly Data Sync Section */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h3 className="text-lg font-semibold mb-4">🔄 Sync Weekly Usage <span className="text-green-600 text-sm font-normal">(hier maak je niks mee stuk)</span></h3>
          
          <p className="mb-4 text-sm">
            <strong>Uitleg:</strong> Hiermee haal je het wekelijkse verbruik van ieder appartement op bij Ista. We gebruiken dit om de voorschotten te bepalen en individueel gasverbruik te monitoren per appartement van de Toren.
          </p>
          
          <div className="mb-4">
            <label className="block mb-1 font-semibold text-left">From:</label>
            <input 
              type="date" 
              value={from} 
              onChange={e => setFrom(e.target.value)} 
              className="date-input"
              max={today.format('YYYY-MM-DD')}
            />
          </div>
      
          <div className="mb-6">
            <label className="block mb-1 font-semibold text-left">To:</label>
            <input 
              type="date" 
              value={to} 
              onChange={e => setTo(e.target.value)} 
              className="date-input"
              max={oneWeekAgo.format('YYYY-MM-DD')}
            />
            <p className="text-xs text-gray-600 mt-1">
              Note: End date is limited to 7 days ago to avoid errors with incomplete current week data.
            </p>
          </div>
          
          {syncProgress && (
            <div className="mb-4">
              <p className="text-sm text-gray-700 mb-1">
                Verwerken: week van <strong>{syncProgress.label}</strong> ({syncProgress.current}/{syncProgress.total})
              </p>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-green-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${(syncProgress.current / syncProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          <button
            onClick={handleSync}
            disabled={isLoading}
            className="home-button"
          >
            {isLoading ? 'Verwerken...' : '🔄 Sync Weekly Usage'}
          </button>
          
          {/* Show logs directly below the weekly section when it's active */}
          {activeSection === 'weekly' && renderLogSection()}
        </div>
        
        {/* Energy Year Import Section */}
        <div className="bg-white p-4 rounded-lg shadow mb-6 border-l-4 border-orange-400">
          <h3 className="text-lg font-semibold mb-4">📅 Import Complete Energy Year <span className="text-orange-600 text-sm font-normal">(opletten)</span></h3>
          
          <p className="mb-4 text-sm">
            <strong>Uitleg:</strong> Hiermee halen we het maandelijks verbruik op bij Ista. <strong className="text-orange-600">Let op: alleen op klikken als je zeker weet dat je oude data wilt deleten en nieuwe data wilt importeren.</strong>
          </p>
          
          <div className="mb-4">
            <label className="block mb-1 font-semibold text-left">Energy Year:</label>
            <select 
              value={energyYearToImport}
              onChange={e => setEnergyYearToImport(parseInt(e.target.value))}
              className="date-input"
            >
              {generateYearOptions(currentYear - 5).map(option => option)}
            </select>
            <span className="ml-2 text-gray-600">({getEnergyYearLabel(energyYearToImport)})</span>
          </div>
          
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={clearExisting}
                onChange={e => setClearExisting(e.target.checked)}
                className="mr-2"
              />
              Clear existing data for this period first
            </label>
          </div>
          
          <button 
            onClick={handleImportEnergyYear} 
            disabled={isLoading} 
            className="home-button bg-green-700 hover:bg-green-800"
          >
            {isLoading ? 'Importing...' : '📥 Import Energy Year Data'}
          </button>
          
          <p className="mt-2 text-sm text-gray-600">
            This will import all months of the selected energy year (Jul-Jun).
          </p>
          
          {/* Show logs directly below the energy year section when it's active */}
          {activeSection === 'energyYear' && renderLogSection()}
        </div>
        
        {/* June 30 Import Section with Year Selection */}
        <div className="bg-white p-4 rounded-lg shadow mb-6 border-l-4 border-orange-400">
          <h3 className="text-lg font-semibold mb-4">📊 June 30 Yearly Totals Import <span className="text-orange-600 text-sm font-normal">(opletten)</span></h3>
          
          <p className="mb-4 text-sm">
            <strong>Uitleg:</strong> Op 30 juni ieder jaar verstuurt Ista in de API de jaarlijkse totalen (eenheden) van ieder appartement. Met deze API halen we deze gegevens op. <strong className="text-orange-600">Dit moet uiteraard wel gecheckt worden, omdat het vrij lastig is om deze totalen te lezen.</strong>
          </p>
          
          <div className="mb-4">
            <label className="block mb-1 font-semibold text-left">Year (June 30):</label>
            <select 
              value={june30Year}
              onChange={e => setJune30Year(parseInt(e.target.value))}
              className="date-input"
            >
              {/* Allow selection from 2020 up to the current year plus 1 (for future prep) */}
              {generateYearOptions(2020, currentYear + 1)}
            </select>
            <span className="ml-2 text-gray-600">
              (End of {getEnergyYearLabel(june30Year - 1)} energy year)
            </span>
          </div>
          
          <div className="mb-4">
            <label className="flex items-center mb-2">
              <input
                type="checkbox"
                checked={forceTotals}
                onChange={e => setForceTotals(e.target.checked)}
                className="mr-2"
              />
              Force mark as yearly totals (even if not detected automatically)
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={yearlyTotalsOnly}
                onChange={e => setYearlyTotalsOnly(e.target.checked)}
                className="mr-2"
              />
              Import yearly totals only (skip regular daily data)
            </label>
          </div>
          
          <button 
            onClick={handleJune30Import} 
            disabled={isLoading} 
            className="home-button bg-yellow-600 hover:bg-yellow-700"
          >
            {isLoading ? 'Importing...' : `📅 Import June 30, ${june30Year} Totals`}
          </button>
          
          <p className="mt-2 text-sm text-gray-600">
            This imports the official end-of-year totals for energy accounting purposes.
            These values are essential for accurate yearly comparisons.
          </p>
          
          {/* Show logs directly below the June 30 section when it's active */}
          {activeSection === 'june30' && renderLogSection()}
        </div>
      </div>
    </div>
  );
};  

export default IstaAdminPanel;